import './your-access-card.scss';

import { useEffect, useRef, useState } from 'react';

import { readActionClaims } from '@/authz/authority';
import { type IDelegation, useDelegation } from '@/authz/delegation';
import { describeAccess } from '@/authz/describe-access';
import { type ITeamSpendPosition } from '@/authz/team-spend';
import { useJwtSnapshot } from '@/authz/token-store';
import { TypeButton } from '@/components/reusable/button';
import SupportBadge from '@/components/reusable/support-badge';
import { getUserData, KEEP_ACROSS_LOGOUT_PREFIX } from '@/constants/global';
import { useTeamSpendPosition } from '@/hooks/queries/team-access/use-team-spend';

/**
 * Kept across sign-outs: `onLogout` clears localStorage but preserves this prefix, so "Got it"
 * really means "don't show me this again" rather than "until I next sign in".
 */
export const ACCESS_CARD_KEY_PREFIX = `${KEEP_ACROSS_LOGOUT_PREFIX}team-access-card-seen:`;

export const accessCardKey = (delegation: IDelegation, ownUserId?: string | null): string =>
	`${ACCESS_CARD_KEY_PREFIX}${delegation.delegateId ?? ownUserId ?? 'unknown'}`;

const readSeen = (key: string): boolean => {
	try {
		return localStorage.getItem(key) === '1';
	} catch {
		return false;
	}
};

const writeSeen = (key: string) => {
	try {
		localStorage.setItem(key, '1');
	} catch {
		// Storage blocked: the card comes back next visit, which is the safe failure.
	}
};

export interface YourAccessCardViewProps {
	delegation: IDelegation;
	/** The member's own full name, from their token. */
	memberName?: string | null;
	actionKeys: readonly string[];
	/**
	 * The member's own allowance from `SpendAuthorization/me`. The token says only whether they may
	 * spend, not how much, so without this the card must not claim "no limit set".
	 */
	spendPosition?: Pick<ITeamSpendPosition, 'dailyCreditLimit' | 'electionCreditLimit'> | null;
	onDismiss: () => void;
}

export const YourAccessCardView = ({ delegation, memberName, actionKeys, spendPosition, onDismiss }: YourAccessCardViewProps) => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const owner = delegation.ownerName || 'the account owner';
	const firstName = memberName?.trim().split(/\s+/)[0] || null;
	const sentence = describeAccess({
		firstName: 'You',
		actionKeys,
		canSpendCredits: delegation.canSpendCredits,
		dailyCreditLimit: spendPosition?.dailyCreditLimit ?? null,
		electionCreditLimit: spendPosition?.electionCreditLimit ?? null,
		creditLimitsUnknown: !spendPosition,
		forLabel: owner,
	})
		// describeAccess speaks about a member in the third person; this card speaks to them.
		.replace(/^You has /, 'You have ');

	useEffect(() => {
		buttonRef.current?.focus();
	}, []);

	return (
		<div className='your-access-card__backdrop'>
			<section
				className='your-access-card'
				role='dialog'
				aria-modal='true'
				aria-labelledby='your-access-card-title'
				aria-describedby='your-access-card-sentence'
				onKeyDown={(e) => {
					if (e.key === 'Escape') onDismiss();
				}}
			>
				<p className='your-access-card__eyebrow'>Team access</p>
				<h2
					id='your-access-card-title'
					className='your-access-card__title'
				>
					{firstName ? `Welcome, ${firstName}` : 'Welcome'}
				</h2>

				{delegation.supportNumber !== null && (
					<div className='your-access-card__badge'>
						<SupportBadge
							supportNumber={delegation.supportNumber}
							name={memberName}
							variant='full'
						/>
					</div>
				)}

				<p className='your-access-card__owner'>
					You&rsquo;re working in <strong>{owner}</strong>&rsquo;s account
					{delegation.ownerRole ? ` (${delegation.ownerRole})` : ''}. What you do here is recorded as you, with your badge.
				</p>

				<h3 className='your-access-card__heading'>Your access</h3>
				<p
					id='your-access-card-sentence'
					className='your-access-card__sentence'
				>
					{sentence}
				</p>
				<p className='your-access-card__note'>Need more? Ask {owner} to change your access.</p>

				<div className='your-access-card__actions'>
					<TypeButton
						ref={buttonRef}
						title='Got it'
						buttonType='bold'
						buttonSize='small'
						onClick={onDismiss}
					/>
				</div>
			</section>
		</div>
	);
};

/** Shows once per team member until they press "Got it". */
const YourAccessCard = () => {
	const delegation = useDelegation();
	const snapshot = useJwtSnapshot();
	const user = getUserData();
	const key = accessCardKey(delegation, user.user?.UserId);
	const [seen, setSeen] = useState(() => readSeen(key));
	const spend = useTeamSpendPosition(delegation.isTeamMember && delegation.canSpendCredits && !seen);

	useEffect(() => {
		setSeen(readSeen(key));
	}, [key]);

	if (!delegation.isTeamMember || seen) return null;

	// The member's own grants, exactly as the token carries them; the snapshot is the fallback.
	// Wallet keys are left out while P2 hides the wallet from team members, so the card never
	// promises a screen they cannot open. Drop the filter when P3 ships wallet use.
	const actionKeys = ((user.token ? readActionClaims(user.token) : null) ?? [...snapshot.actions]).filter(
		(key) => !key.toLowerCase().startsWith('wallet_')
	);

	return (
		<YourAccessCardView
			delegation={delegation}
			memberName={user.user?.FullName}
			actionKeys={actionKeys}
			spendPosition={spend.data ?? null}
			onDismiss={() => {
				writeSeen(key);
				setSeen(true);
			}}
		/>
	);
};

export default YourAccessCard;
